import { useContext, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';

import { DealStateLabelMap } from '../../../../gql/dealGql';
import { PodUser, TeamType, usePodsQuery } from '../../../../gql/generated/graphql';

import { Option } from '../../../shared/types';

import ClearFiltersButton from './ClearFiltersButton';
import DownloadButton from './DownloadButton';
import FilterInput from './FilterInput';
import MoreFilters from './MoreFilters';
import MultiAutocomplete from './MultiAutocomplete';
import Search from './Search';

import { TITLING_STATES } from '../../../../constants/dealStates';
import { FiltersContext } from '../../../../libs/contextLib';
import { hasKey } from '../../../../utils/objects';
import { FilterOptionsEnum, FiltersActionKind, filterOptions } from '../../utils';

const DEAL_STATE = 'Deal State';

type TableFilterProps = {
  maxAge: string | undefined;
  titlingPodUsers: PodUser[];
};

const TableFilters = ({ maxAge, titlingPodUsers }: TableFilterProps) => {
  const {
    filters: { titling: titlingFilters },
    dispatch: dispatchFilters,
  } = useContext(FiltersContext);

  const getActiveFilters = () => {
    const filterArr: FilterOptionsEnum[] = [];
    filterOptions.map((fo) => {
      if (hasKey(titlingFilters, fo.value)) {
        if (titlingFilters[fo.value]) {
          filterArr.push(fo.label);
        }
      }
      if (fo.toValue) {
        if (hasKey(titlingFilters, fo.toValue)) {
          if (titlingFilters[fo.toValue]) {
            filterArr.push(fo.label);
          }
        }
      }
      return filterArr;
    });
    return filterArr;
  };

  const [selectedFilters, setSelectedFilters] = useState<FilterOptionsEnum[]>(() =>
    getActiveFilters(),
  );

  const [titlingPods, setTitlingPods] = useState<Option[]>([]);

  usePodsQuery({
    variables: { team_type: [TeamType.Titling] },
    onCompleted: (data) => {
      if (data?.pods) {
        const titlingPodsAll = [...data.pods];

        titlingPodsAll.unshift({
          id: undefined,
          name: 'No Team Assigned',
        });

        setTitlingPods(
          titlingPodsAll.map((titlingPod) => ({
            value: titlingPod?.id?.toString() ?? '',
            label: titlingPod?.name ?? '',
          })),
        );
      }
    },
  });

  const userOptions = useMemo(() => {
    const users = [...titlingPodUsers];
    users.unshift({
      user_id: 'blank',
      user: {
        id: 'blank',
        name: 'Not Assigned',
        email: '',
        nickname: '',
        hours: '',
        on_vacation: false,
        auto_assign_deals: false,
      },
    });
    return users.map((titlingUser) => ({
      value: titlingUser.user_id ?? '',
      label: titlingUser.user?.name ?? '',
    }));
  }, [titlingPodUsers]);

  const titlingStatesOptions = useMemo(
    () => TITLING_STATES.map((value) => ({ value, label: DealStateLabelMap[value] })),
    [],
  );

  return (
    <Flex direction="column" py={1} width="full">
      <Flex align="center" justify="flex-start" w="full" gap={2}>
        <Search dispatch={dispatchFilters} value={titlingFilters.search} />
        <MultiAutocomplete
          placeholder="Team"
          options={titlingPods}
          actionKind={FiltersActionKind.SET_TITLING_PODS}
          dispatch={dispatchFilters}
          value={titlingFilters.titlingPods}
        />
        <MultiAutocomplete
          placeholder="Assigned to"
          options={userOptions}
          actionKind={FiltersActionKind.SET_ASSIGNEES}
          dispatch={dispatchFilters}
          value={titlingFilters.assignees}
        />
        <MultiAutocomplete
          placeholder={DEAL_STATE}
          options={titlingStatesOptions}
          dispatch={dispatchFilters}
          actionKind={FiltersActionKind.SET_DEAL_STATE}
          value={titlingFilters.dealStates}
        />
        <MoreFilters
          options={filterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          dispatch={dispatchFilters}
        />
        <ClearFiltersButton />
        <Box flexGrow={1} />
        <DownloadButton />
      </Flex>
      {filterOptions.length && selectedFilters.length ? (
        <Flex w="100%" overflow="visible" align="center" pt={2} gap={2}>
          {filterOptions.map((fo) => {
            if (selectedFilters?.includes(fo.label)) {
              return (
                <FilterInput
                  key={fo.label}
                  option={fo}
                  maxAge={maxAge}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              );
            }
            return null;
          })}
        </Flex>
      ) : null}
    </Flex>
  );
};

export default TableFilters;
